import { z } from 'zod'
import { PaginationResponse } from '@/types'

export const NeighborhoodStatsSchema = z
	.object({
		dim_subgeo_id: z.string(),
		gbv_prev_1yr: z.number(),
		nights_stay_prev_1yr: z.number(),
		occupancy_prev_1yr: z.number()
	})
	.optional()

// A plain union tries the Polygon branch first on every MultiPolygon and
// records one issue per coordinate: on large rings (Tasmania, 170k points)
// that overflows the call stack and the whole subgeos response is dropped.
export const GeoJSONGeometrySchema = z.discriminatedUnion('type', [
	z
		.object({
			type: z.literal('Polygon'),
			coordinates: z.array(z.array(z.array(z.number())))
		})
		.passthrough(),
	z
		.object({
			type: z.literal('MultiPolygon'),
			coordinates: z.array(z.array(z.array(z.array(z.number()))))
		})
		.passthrough()
])

export const NeighborhoodSchema = z.object({
	dim_geo: z.string(),
	acp_id: z.string(),
	place_name: z.string(),
	dim_geom: GeoJSONGeometrySchema
})

export const SubgeoSchema = z.object({
	id: z.string(),
	name: z.string(),
	neighborhoodIds: z.array(z.string()),
	color: z.string()
})

export const GeoSelectionSchema = z.object({
	geo: z.string(),
	neighborhoods: z.array(NeighborhoodSchema),
	subgeos: z.array(SubgeoSchema)
})

export const Geo = z.object({
	id: z.string(),
	geoCode: z.string(),
	geoMappingFileId: z.string(),
	createdAt: z.string(),
	updatedAt: z.string()
})

export const PaginatedGeoResponse = z.object({
	meta: PaginationResponse,
	data: z.array(Geo)
})

export const GetNeighborhoodsSchema = z.object({
	id: z.string(),
	geoMappingGeoId: z.string(),
	geoMappingSubGeoId: z.string().nullable(),
	geoCode: z.string(),
	name: z.string(),
	geometry: GeoJSONGeometrySchema,
	geoMappingFileId: z.string(),
	createdAt: z.string(),
	updatedAt: z.string()
})

export const PaginatedNeighborhoodResponse = z.object({
	meta: PaginationResponse,
	data: z.array(GetNeighborhoodsSchema)
})

export const GetSubgeosSchema = z.object({
	id: z.string(),
	geoMappingGeoId: z.string(),
	geoCode: z.string(),
	name: z.string(),
	geometry: GeoJSONGeometrySchema.nullable(),
	geoMappingFileId: z.string(),
	createdAt: z.string(),
	updatedAt: z.string(),
	neighborhoods: z.array(
		z.object({
			id: z.string(),
			geoMappingGeoId: z.string(),
			geoMappingSubGeoId: z.string(),
			geoCode: z.string(),
			name: z.string(),
			geoMappingFileId: z.string(),
			createdAt: z.string(),
			updatedAt: z.string()
		})
	)
})

export const NeighborhoodWithSubGeoSchema = z.object({
	id: z.string(),
	geoMappingGeoId: z.string(),
	geoMappingSubGeoId: z.string(),
	geoCode: z.string(),
	name: z.string(),
	geometry: GeoJSONGeometrySchema,
	geoMappingFileId: z.string(),
	createdAt: z.string(),
	updatedAt: z.string(),
	subGeo: z.object({
		id: z.string(),
		geoMappingGeoId: z.string(),
		geoCode: z.string(),
		name: z.string(),
		geoMappingFileId: z.string(),
		createdAt: z.string(),
		updatedAt: z.string()
	})
})

export const SubGeoSearchParamsSchema = z.object({
	geo: z.string().optional()
})

export const transformApiNeighborhoodToNeighborhood = (
	apiNeighborhood: ApiNeighborhood
): Neighborhood => ({
	dim_geo: apiNeighborhood.geoCode,
	acp_id: apiNeighborhood.id,
	place_name: apiNeighborhood.name,
	dim_geom: apiNeighborhood.geometry
})

export const transformApiSubgeoToSubgeo = (
	apiSubgeo: ApiSubgeo,
	color?: string
): Subgeo => ({
	id: apiSubgeo.id,
	name: apiSubgeo.name,
	neighborhoodIds: apiSubgeo.neighborhoods.map((n) => n.id),
	color: color ?? '#3B82F6'
})

export type NeighborhoodStats = z.infer<typeof NeighborhoodStatsSchema>
export type GeoJSONGeometry = z.infer<typeof GeoJSONGeometrySchema>
export type Neighborhood = z.infer<typeof NeighborhoodSchema>
export type Subgeo = z.infer<typeof SubgeoSchema>
export type GeoSelection = z.infer<typeof GeoSelectionSchema>
export type Geo = z.infer<typeof Geo>
export type PaginatedGeoResponse = z.infer<typeof PaginatedGeoResponse>
export type ApiNeighborhood = z.infer<typeof GetNeighborhoodsSchema>
export type ApiSubgeo = z.infer<typeof GetSubgeosSchema>
export type ApiNeighborhoodWithSubGeo = z.infer<
	typeof NeighborhoodWithSubGeoSchema
>
export type PaginatedNeighborhoodResponse = z.infer<
	typeof PaginatedNeighborhoodResponse
>
export type SubGeoSearchParams = z.infer<typeof SubGeoSearchParamsSchema>
